<template>
  <div class="transfer">
    <div class="transfer__form">
      <div class="transfer__row transfer__row--1">
        <app-form-group label="From" required>
          <app-select
            @search="onLocationSearch"
            v-model="form.origin"
            :options="location_options"
            :filterable="false"
            :clearable="false"
            label="full_name"
            placeholder="Select"
            :error="$v.form.origin.$error"
          />
          <template #error>
            <div v-if="$v.form.origin.$dirty && !$v.form.origin.required">Required field</div>
          </template>
        </app-form-group>
        <app-form-group label="To" required>
          <app-select
            @search="onLocationSearch"
            v-model="form.destination"
            :options="location_options"
            :filterable="false"
            :clearable="false"
            label="full_name"
            placeholder="Select"
            :error="$v.form.destination.$error"
          />
          <template #error>
            <div v-if="$v.form.destination.$dirty && !$v.form.destination.required">Required field</div>
          </template>
        </app-form-group>
        <app-form-group label="Round trip" class="form-group--switcher">
          <app-switcher v-model="form.round_trip" />
        </app-form-group>
        <app-form-group v-show="form.round_trip" label="Waiting hours" label-for="waiting">
          <app-input
            v-model.number="form.waiting_hours"
            id="waiting"
            type="number"
            placeholder="Type"
            min="0"
            name="waiting"
            :error="$v.form.waiting_hours.$error"
          />
          <template #error>
            <div v-if="$v.form.waiting_hours.$dirty && !$v.form.waiting_hours.required">Required field</div>
          </template>
        </app-form-group>
      </div>
      <div class="transfer__row transfer__row--2">
        <app-form-group label="Pax" label-for="pax" required>
          <app-input
            v-model.number="form.pax"
            id="pax"
            type="number"
            placeholder="Type"
            min="0"
            name="pax"
            :error="$v.form.pax.$error"
            @change.native="onSetCarsCount"
          />
          <template #error>
            <div v-if="$v.form.pax.$dirty && !$v.form.pax.required">Required field</div>
          </template>
        </app-form-group>
        <app-form-group label="Luggage" class="form-group--switcher">
          <app-switcher v-model="form.luggage" @change="onSetCarsCount" />
        </app-form-group>
        <app-form-group label="Car model" required>
          <app-select
            @input="onSetCarsCount"
            v-model="form.car_model"
            :options="car_model_options"
            :clearable="false"
            placeholder="Select"
            :error="$v.form.car_model.$error"
          />
          <template #error>
            <div v-if="$v.form.car_model.$dirty && !$v.form.car_model.required">Required field</div>
          </template>
        </app-form-group>
        <app-form-group label="Cars count" label-for="cars_count" required>
          <app-input
            v-model.number="form.cars_count"
            id="cars_count"
            type="number"
            placeholder="Type"
            min="0"
            name="cars_count"
            :error="$v.form.cars_count.$error"
          />
          <template #error>
            <div v-if="$v.form.cars_count.$dirty && !$v.form.cars_count.required">Required field</div>
          </template>
        </app-form-group>
      </div>
      <app-button @click.native="onCalculate" :disabled="$v.form.$error" ref="calculate">Calculate</app-button>
    </div>
    <template v-if="calculation_result">
      <app-cells>
        <h2 class="title">Calculation result</h2>
        <button class="transfer__print">
          <img src="@/assets/img/print-icon.svg" alt="Print">
          <span>Print</span>
        </button>
      </app-cells>
      <div class="transfer__table">
        <div class="transfer__table-head">
          <span>Destination</span>
          <span>Distance</span>
          <span>Duration</span>
          <span>Price</span>
        </div>
        <div
          v-for="(point, index) of calculation_result.points"
          :key="index"
          class="transfer__table-block"
        >
          <div v-if="point.price_waiting || point.waiting_hours" class="transfer__table-row">
            <div class="transfer__table-info">
              <img src="@/assets/img/clock-icon.svg" alt="Time">
              <span class="transfer__table-name transfer__table-name--waiting">
                Waiting:
              </span>
            </div>
            <span></span>
            <span class="transfer__table-time">{{ point.waiting_hours }}</span>
            <span class="transfer__table-price">{{ point.price_waiting }}$</span>
          </div>
          <div class="transfer__table-row">
            <div class="transfer__table-info">
              <img src="@/assets/img/car-icon.svg" alt="Car">
              <span class="transfer__table-name">
                <span>{{ point.origin }}</span>
                <img src="@/assets/img/arrow-transfer.svg" alt="Arrow">
                <span>{{ point.destination }}</span>
              </span>
            </div>
            <span class="transfer__table-distance">{{ point.distance }}</span>
            <span class="transfer__table-time">{{ point.time }}</span>
            <span class="transfer__table-price">{{ point.price_driving }}$</span>
          </div>
        </div>
        <div class="transfer__table-row transfer__table-row--total">
          <div class="transfer__table-info">
            <img src="@/assets/img/total-icon.svg" alt="Total">
            <span class="transfer__table-name">
              TOTAL:
            </span>
          </div>
          <span class="transfer__table-distance">{{ calculation_result.total.distance }}</span>
          <span class="transfer__table-time">{{ calculation_result.total.time }}</span>
          <span class="transfer__table-price">{{ calculation_result.total.price }}$</span>
        </div>
      </div>
    </template>
  </div>
</template>

<script>
import { calculateTransferPrice, getCarManagementCarModels, getGoogleSearch } from '@/http'
import debounce from 'lodash/debounce'
import { required, requiredIf } from 'vuelidate/lib/validators'

export default {
  name: 'TransferRegular',
  data() {
    return {
      form: {
        round_trip: false,
        luggage: true,
        waiting_hours: 0,
        cars_count: null,
      },
      location_options: [],
      car_model_options: [],
      calculation_result: null,
    }
  },
  validations: {
    form: {
      origin: { required },
      destination: { required },
      car_model: { required },
      cars_count: { required },
      pax: { required },
      waiting_hours: {
        required: requiredIf(function() {
          return this.form.round_trip
        })
      },
    }
  },
  created() {
    getCarManagementCarModels(true).then(response => { this.car_model_options = response.data })
  },
  methods: {
    onSetCarsCount() {
      if (!this.form.pax || !this.form.car_model) return
      if (this.form.luggage) this.form.cars_count = Math.ceil(this.form.pax / this.form.car_model.max_pax_luggage)
      else this.form.cars_count = Math.ceil(this.form.pax / this.form.car_model.max_pax)
    },
    onCalculate() {
      this.$v.form.$touch()
      if (this.$v.$invalid) {
        this.$notify({
          type: 'info',
          title: 'Check that the form fields are filled in correctly',
        })
      } else {
        this.$refs.calculate.preload = true
        calculateTransferPrice(this.formatDataForCalc())
          .then(response => {
            this.calculation_result = response.data
          })
          .finally(() => {
            this.$refs.calculate.preload = false
          })
      }
    },
    formatDataForCalc() {
      return {
        origin: {
          place_id: this.form.origin.place_id,
          place_name: this.form.origin.formatted_name,
          waiting_hours: 0,
        },
        points: [
          {
            place_id: this.form.destination.place_id,
            place_name: this.form.destination.formatted_name,
            waiting_hours: this.form.round_trip ? this.form.waiting_hours : 0
          }
        ],
        car_model: this.form.car_model.id,
        cars_count: this.form.cars_count,
        round_trip: this.form.round_trip,
        luggage: this.form.luggage,
        pax: this.form.pax,
      }
    },
    onLocationSearch(search, loading) {
      loading(true)
      this.searchLocation(loading, search, this)
    },
    searchLocation: debounce((loading, search, vm) => {
      if (!search) {
        loading(false)
        vm.location_options = []
      } else {
        getGoogleSearch(search).then(response => {
          vm.location_options = response.data
          loading(false)
        })
      }
    }, 350),
  }
}
</script>
